//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  TScheduledPostConfig,
  UniversalScheduledPostConfigType,
} from '@apis/universal-posts/config/universalScheduledPostConfigModel';
import {
  isFacebookPageFeedPostConfig,
  isInstagramFeedAutomaticPostConfig,
  isInstagramFeedManualPostConfig,
  isInstagramReelsAutomaticPostConfig,
  isInstagramReelsManualPostConfig,
  isInstagramStoryGroupAutomaticPostConfig,
  isInstagramStoryGroupPostConfig,
  isScheduledOrganicPinConfig,
  isTikTokVideoAutomaticPostConfig,
  isTwitterMediaPostConfig,
  isYouTubeAutomaticPostConfig,
} from '@apis/universal-posts/config/universalScheduledPostsConfigUtils';
import { IDraftPost } from '@apis/universal-posts/draft-posts/universalDraftPostsApiModel';
import { IScheduledPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import {
  TUniversalPostItem,
  UniversalPostType,
  UniversalPostTypeValues,
} from '@apis/universal-posts/universalPostsApiModel';
import { FACEBOOK_MAX_TEXT_LENGTH } from '@facebook/constants/facebookConstants';
import Cuid from '@models/Cuid';
import FileId from '@models/FileId';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import {
  isDefinedWithNetworkItem,
  isVideo,
  isYouTube,
} from '@models/INetworkItem';
import PostableStatus from '@models/PostableStatus';
import { SocialNetwork } from '@models/SocialNetwork';
import { UniversalPostableCustomStatus } from '@models/UniversalPostableCustomStatus';
import {
  MAX_PIN_DESCRIPTION_LENGTH,
  MAX_PIN_TITLE_LENGTH,
} from '@pinterest/redux/post-details/pinterestPostDetailsModel';
import { CHARACTERS_REMAINING_THRESHOLD } from '@universal-scheduling/components/calendar/UniversalCalendarConstants';
import { MAX_IG_MESSAGE_LENGTH } from '@universal-scheduling/components/scheduler/post-details-forms/instagram/InstagramPostDetailsConstants';
import { MAX_TIKTOK_CAPTION_LENGTH } from '@universal-scheduling/components/scheduler/post-details-forms/tiktok/TikTokPostDetailsConstants';
import { MAX_TWEET_LENGTH } from '@universal-scheduling/components/scheduler/post-details-forms/twitter/TwitterPostDetailsConstants';
import {
  YOUTUBE_MAX_DESCRIPTION_LENGTH,
  YOUTUBE_MAX_TITLE_LENGTH,
} from '@universal-scheduling/components/scheduler/post-details-forms/youtube/YouTubePostDetailsConstants';
import { getThumbnailOffsetInSeconds } from '@universal-scheduling/components/scheduler/schedulerUtils';
import { SchedulerSocialNetwork } from '@universal-scheduling/models/SocialNetworkModels';
import { getIsProcessingCompletePosted } from '@universal-scheduling/redux/calendar/universalCalendarUtils';
import { isDefined } from '@utils/filterPredicates';
import { getMedsStandardVideoUrl } from '@utils/meds';
import { pluralize } from '@utils/stringUtils';

import { RoutableStateName } from 'app/top-level-states';

export interface IStandardPostDetails {
  caption?: string;
  coverFileId?: FileId;
  cuids?: Cuid[];
  isManual: boolean;
  scheduledTime?: Date;
  title?: string;
  url?: string;
  userTags?: string[];
}

export const getStandardPostDetails = (
  post: IScheduledPost | IDraftPost,
): IStandardPostDetails => {
  const config = post.config;

  const baseItem = {
    scheduledTime: post.scheduledTime,
    cuids: config.cuids,
    isManual: false,
  };

  if (isScheduledOrganicPinConfig(config)) {
    return {
      ...baseItem,
      caption: config.description,
      coverFileId: config.coverFileId,
      title: config.title,
      url: config.url,
    };
  } else if (isFacebookPageFeedPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.status,
      url: config.link,
    };
  } else if (isInstagramFeedAutomaticPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.caption,
      userTags: config.userTags?.map((u) => u.instagramUsername),
      coverFileId: config.videoMetadata?.coverFileId,
    };
  } else if (isInstagramFeedManualPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.caption,
      isManual: true,
    };
  } else if (isInstagramReelsAutomaticPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.customizations?.caption,
      userTags: config.customizations?.userTags?.split(/\s|,/),
      coverFileId: config.coverFileId,
    };
  } else if (isInstagramReelsManualPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.customizations?.caption,
      userTags: config.customizations?.userTags?.split(/\s|,/),
      isManual: true,
    };
  } else if (isInstagramStoryGroupAutomaticPostConfig(config)) {
    return {
      ...baseItem,
    };
  } else if (isInstagramStoryGroupPostConfig(config)) {
    return {
      ...baseItem,
      isManual: true,
    };
  } else if (isTikTokVideoAutomaticPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.caption,
    };
  } else if (isTwitterMediaPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.text,
    };
  } else if (isYouTubeAutomaticPostConfig(config)) {
    return {
      ...baseItem,
      caption: config.description,
      coverFileId: config.coverFileId,
      title: config.title,
    };
  }

  return baseItem;
};

export const getPostConfigWithNewCaption = (
  config: TScheduledPostConfig,
  caption: string,
): { config: TScheduledPostConfig; hasError: boolean } => {
  const maxTextLength = getTextMaxLength(config) ?? 0;
  const postDetailsWithError = { hasError: caption.length > maxTextLength };

  if (isScheduledOrganicPinConfig(config)) {
    return {
      ...postDetailsWithError,
      config: { ...config, description: caption },
    };
  } else if (isFacebookPageFeedPostConfig(config)) {
    return { ...postDetailsWithError, config: { ...config, status: caption } };
  } else if (
    isInstagramFeedAutomaticPostConfig(config) ||
    isInstagramFeedManualPostConfig(config) ||
    isTikTokVideoAutomaticPostConfig(config)
  ) {
    return { ...postDetailsWithError, config: { ...config, caption } };
  } else if (
    isInstagramReelsAutomaticPostConfig(config) ||
    isInstagramReelsManualPostConfig(config)
  ) {
    return {
      ...postDetailsWithError,
      config: {
        ...config,
        customizations: { ...config.customizations, caption },
      },
    };
  } else if (isTwitterMediaPostConfig(config)) {
    return { ...postDetailsWithError, config: { ...config, text: caption } };
  } else if (isYouTubeAutomaticPostConfig(config)) {
    return {
      ...postDetailsWithError,
      config: { ...config, description: caption },
    };
  }

  return { ...postDetailsWithError, config: { ...config } };
};

export const getTextMaxLength = (config: TScheduledPostConfig) => {
  if (
    isInstagramReelsAutomaticPostConfig(config) ||
    isInstagramReelsManualPostConfig(config) ||
    isInstagramFeedAutomaticPostConfig(config) ||
    isInstagramFeedManualPostConfig(config)
  ) {
    return MAX_IG_MESSAGE_LENGTH;
  }

  if (isTikTokVideoAutomaticPostConfig(config)) {
    return MAX_TIKTOK_CAPTION_LENGTH;
  }

  if (isTwitterMediaPostConfig(config)) {
    return MAX_TWEET_LENGTH;
  }

  if (isScheduledOrganicPinConfig(config)) {
    return MAX_PIN_DESCRIPTION_LENGTH;
  }

  if (isFacebookPageFeedPostConfig(config)) {
    return FACEBOOK_MAX_TEXT_LENGTH;
  }

  if (isYouTubeAutomaticPostConfig(config)) {
    return YOUTUBE_MAX_DESCRIPTION_LENGTH;
  }
};

export const getMaxTitleLength = (config: TScheduledPostConfig) => {
  if (isYouTubeAutomaticPostConfig(config)) {
    return YOUTUBE_MAX_TITLE_LENGTH;
  }

  if (isScheduledOrganicPinConfig(config)) {
    return MAX_PIN_TITLE_LENGTH;
  }
};

export const getCharactersRemainingCount = (
  maxLength: number,
  textLength: number,
) => {
  if (maxLength - textLength <= CHARACTERS_REMAINING_THRESHOLD) {
    return `${pluralize(maxLength - textLength, 'character')} remaining`;
  }
};

export const mapPostConfigToPostType = (
  type: UniversalScheduledPostConfigType,
): UniversalPostType => {
  switch (type) {
    case UniversalScheduledPostConfigType.TikTokVideoAutomaticPost:
      return UniversalPostType.TikTokVideoAutomaticPost;
    case UniversalScheduledPostConfigType.FacebookPageFeedLinkPost:
    case UniversalScheduledPostConfigType.FacebookPageFeedVideoPost:
    case UniversalScheduledPostConfigType.FacebookPageFeedPhotoPost:
      return UniversalPostType.FacebookPageFeedPost;
    case UniversalScheduledPostConfigType.PinterestOrganicPin:
      return UniversalPostType.PinterestOrganicPin;
    case UniversalScheduledPostConfigType.InstagramFeedAutomaticPost:
      return UniversalPostType.InstagramFeedAutomaticPost;
    case UniversalScheduledPostConfigType.InstagramFeedManualPost:
      return UniversalPostType.InstagramFeedManualPost;
    case UniversalScheduledPostConfigType.InstagramReelsAutomaticPost:
      return UniversalPostType.InstagramReelsAutomaticPost;
    case UniversalScheduledPostConfigType.InstagramReelsManualPost:
      return UniversalPostType.InstagramReelsManualPost;
    case UniversalScheduledPostConfigType.InstagramStoryGroup:
      return UniversalPostType.InstagramStoryGroupPost;
    case UniversalScheduledPostConfigType.InstagramStoryGroupAutomaticPost:
      return UniversalPostType.InstagramStoryGroupAutomaticPost;
    case UniversalScheduledPostConfigType.TwitterMediaPost:
      return UniversalPostType.TwitterMediaPost;
    case UniversalScheduledPostConfigType.YouTubeAutomaticPost:
      return UniversalPostType.YouTubeAutomaticPost;
  }
};

export const getSocialNetworkFromPostType = (
  postType: UniversalPostType,
): SchedulerSocialNetwork => {
  switch (postType) {
    case UniversalPostType.FacebookPageFeedPost:
      return SocialNetwork.Facebook;
    case UniversalPostType.TikTokVideoAutomaticPost:
      return SocialNetwork.TikTok;
    case UniversalPostType.PinterestOrganicPin:
      return SocialNetwork.Pinterest;
    case UniversalPostType.TwitterMediaPost:
      return SocialNetwork.Twitter;
    case UniversalPostType.InstagramFeedAutomaticPost:
    case UniversalPostType.InstagramFeedManualPost:
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramReelsManualPost:
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
    case UniversalPostType.InstagramStoryGroupPost:
      return SocialNetwork.Instagram;
    case UniversalPostType.YouTubeAutomaticPost:
      return SocialNetwork.YouTube;
  }
};

export const getSocialNetworkFromRoute = (
  state: RoutableStateName,
): SocialNetwork => {
  if (state.startsWith('pinterest')) {
    return SocialNetwork.Pinterest;
  } else if (state.startsWith('twitter')) {
    return SocialNetwork.Twitter;
  } else if (state.startsWith('tiktok')) {
    return SocialNetwork.TikTok;
  } else if (state.startsWith('facebook')) {
    return SocialNetwork.Facebook;
  } else if (state.startsWith('instagram')) {
    return SocialNetwork.Instagram;
  }

  return SocialNetwork.Unknown;
};

export const getDefaultPostTypeForSocialNetwork = (
  network: SocialNetwork,
  isAutomatic?: boolean,
): UniversalPostType => {
  switch (network) {
    case SocialNetwork.Facebook:
      return UniversalPostType.FacebookPageFeedPost;
    case SocialNetwork.Instagram:
      return isAutomatic
        ? UniversalPostType.InstagramFeedAutomaticPost
        : UniversalPostType.InstagramFeedManualPost;
    case SocialNetwork.Pinterest:
      return UniversalPostType.PinterestOrganicPin;
    case SocialNetwork.TikTok:
      return UniversalPostType.TikTokVideoAutomaticPost;
    case SocialNetwork.Twitter:
      return UniversalPostType.TwitterMediaPost;
    case SocialNetwork.Unknown:
      throw new Error('SocialNetwork.Unknown not supported');
    case SocialNetwork.Snapchat:
      throw new Error('SocialNetwork.Snapchat not supported');
    case SocialNetwork.Tumblr:
      throw new Error('SocialNetwork.Tumblr not supported');
    case SocialNetwork.YouTube:
      return UniversalPostType.YouTubeAutomaticPost;
    case SocialNetwork.Bazaarvoice:
      throw new Error('SocialNetwork.Bazaarvoice not supported');
  }
};

export const isUniversalPostType = (
  postType: string,
): postType is UniversalPostType => {
  return UniversalPostTypeValues.includes(postType as UniversalPostType);
};

export const isUniversalPostItem = (
  post: IScheduledPost | IDraftPost,
): post is TUniversalPostItem => {
  return isUniversalPostType(post.postType);
};

export const isScheduledPost = (
  post: IScheduledPost | IDraftPost,
): post is IScheduledPost => {
  return !isDraftPost(post);
};

export const isDraftPost = (
  post: IScheduledPost | IDraftPost,
): post is IDraftPost => {
  return !('status' in post);
};

export const getIsPosted = (post: IScheduledPost | IDraftPost) => {
  const postStatus = isScheduledPost(post) ? post.status : undefined;

  return (
    (isScheduledPost(post) &&
      post.customStatus === UniversalPostableCustomStatus.PostHidden) ||
    postStatus === PostableStatus.Posted ||
    (postStatus === PostableStatus.ProcessingComplete &&
      getIsProcessingCompletePosted(post))
  );
};

export const getIsPastDue = (post: IScheduledPost | IDraftPost) => {
  const { isManual, scheduledTime } = getStandardPostDetails(post);

  const isPastScheduledTime = scheduledTime
    ? scheduledTime < new Date()
    : false;

  return (
    isManual &&
    isScheduledPost(post) &&
    isPastScheduledTime &&
    post.customStatus === UniversalPostableCustomStatus.NoAction
  );
};

export const getThumbnailOffsetSeconds = (item: TUniversalPostItem) => {
  switch (item.postType) {
    case UniversalPostType.InstagramReelsAutomaticPost:
      return getThumbnailOffsetInSeconds(item.config.thumbOffset?.length);
    case UniversalPostType.InstagramFeedAutomaticPost:
      return getThumbnailOffsetInSeconds(
        item.config.videoMetadata?.thumbOffset?.length,
      );
    case UniversalPostType.TikTokVideoAutomaticPost:
      return getThumbnailOffsetInSeconds(item.config.thumbnailOffsetInMs);
    default:
      return undefined;
  }
};

export const getVideoUrl = (item: IMlsMediaItem) =>
  isDefinedWithNetworkItem(item) && isVideo(item.networkItem)
    ? getMedsStandardVideoUrl(item.networkItem.videoId)
    : undefined;

export const isReelsPost = (postType: UniversalPostType): boolean => {
  switch (postType) {
    case UniversalPostType.InstagramReelsAutomaticPost:
    case UniversalPostType.InstagramReelsManualPost:
      return true;
    default:
      return false;
  }
};

export const isStoryPost = (postType: UniversalPostType): boolean => {
  switch (postType) {
    case UniversalPostType.InstagramStoryGroupAutomaticPost:
    case UniversalPostType.InstagramStoryGroupPost:
      return true;
    default:
      return false;
  }
};

export const isLinkPost = (item: IScheduledPost | IDraftPost): boolean => {
  const config = item.config;
  if (isFacebookPageFeedPostConfig(config)) {
    return isDefined(config.link) && config.cuids.length < 1;
  }

  return false;
};
